import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE AUDIT CENTRE, AS /sa SEES IT (#1041) — `GET /platform/audit`.
//
// A read-only view of `audit_events`. The server validates every filter,
// decides who may read, and records the search itself (`audit.read`); this
// sends the filters as query parameters and renders the answer. It never
// throws — every outcome is a state the section renders, in the style of the
// other /sa clients — and it carries refusal CODES, never sentences: the
// section picks the words in the reader's language.
// ─────────────────────────────────────────────────────────────────────

/** One row of the trail, as stored. */
export interface AuditEventRow {
  id: string;
  /** ISO instant. */
  at: string;
  organizationId: string | null;
  actorType: string;
  actorId: string | null;
  sessionId: string | null;
  impersonationId: string | null;
  assurance: string | null;
  action: string;
  subjectType: string | null;
  subjectId: string | null;
  /** Shown as stored — whatever the writer put there. */
  detail: unknown;
}

/** What the reader asks for. Every field optional; empty means «any». */
export interface AuditFilters {
  organization?: string;
  actor?: string;
  /** Exact, or a prefix ending in `.` or `*`. */
  action?: string;
  /** ISO instants with an offset — the form converts its local inputs. */
  from?: string;
  to?: string;
  impersonation?: string;
  subject?: string;
  subjectType?: string;
}

export interface AuditPage {
  events: AuditEventRow[];
  /** Absent on the last page. */
  nextCursor: string | null;
  /** Display names for the account ids on the page. */
  accounts: Record<string, { displayName: string | null }>;
  /** Names for the organization ids on the page. */
  organizations: Record<string, { name: string; kind: string }>;
}

export type AuditSearchResult =
  | { state: 'no-api' }
  | { state: 'ok'; page: AuditPage }
  /** The server said no; `field` names the filter it refused, if it did. */
  | { state: 'refused'; status: number; code: string | null; field: string | null }
  | { state: 'error'; status: number | null };

/** The query string for a search. Only non-empty filters are sent. */
export function auditQuery(filters: AuditFilters, cursor?: string | null, limit?: number): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    const v = typeof value === 'string' ? value.trim() : '';
    if (v !== '') params.set(key, v);
  }
  if (limit !== undefined) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const q = params.toString();
  return q ? `?${q}` : '';
}

/** One page of the trail. Every call is recorded by the server as a read. */
export async function searchAudit(
  filters: AuditFilters,
  cursor?: string | null,
): Promise<AuditSearchResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/platform/audit${auditQuery(filters, cursor)}`);
    if (res.ok) return { state: 'ok', page: (await res.json()) as AuditPage };
    if (res.status >= 500) return { state: 'error', status: res.status };
    const body = (await res.json().catch(() => null)) as {
      error?: unknown;
      field?: unknown;
    } | null;
    return {
      state: 'refused',
      status: res.status,
      code: typeof body?.error === 'string' ? body.error : null,
      field: typeof body?.field === 'string' ? body.field : null,
    };
  } catch {
    return { state: 'error', status: null };
  }
}
